import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { NutritionMasterAddPayload } from 'models';
import { useEffect } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LiaTimesSolid } from 'react-icons/lia';
import { toast } from 'react-toastify';
import {
  nutritionUnitMasterActions,
  selectNutritionUnitMasterData,
} from 'store/NutritionUnitMaster/nutritionUnitMasterSlice';
import * as yup from 'yup';
import useNutritionMaster from '../hooks/useNutritionMaster';

interface NutritionMasterFormProps {
  nextDisplayOrder: number;
  onClose: () => void;
  onReflect?: () => void;
}

function NutritionMasterForm({ nextDisplayOrder, onClose, onReflect }: NutritionMasterFormProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // hooks
  const { addNutritionMaster } = useNutritionMaster();

  // selectors
  const nutritionUnitMasterList = useAppSelector(selectNutritionUnitMasterData);

  // side effects
  useEffect(() => {
    dispatch(nutritionUnitMasterActions.fetchNutritionUnitMasterList());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // schema validation
  const schema = yup
    .object({
      nutrition_name: yup.string().required(t('NutritionMaster.warn_field_required')),
      nutrition_unit_master: yup.number().required(t('NutritionMaster.warn_field_required')),
      display_order: yup
        .number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string' && originalValue.trim() === '') {
            return undefined;
          }
          return value;
        })
        .min(1, t('NutritionMaster.warn_number_required'))
        .integer(t('NutritionMaster.warn_number_required'))
        .required(t('NutritionMaster.warn_number_required')),
    })
    .required();

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<NutritionMasterAddPayload>({
    resolver: yupResolver(schema),
  });
  const onSubmit = async (payload: NutritionMasterAddPayload) => {
    const { data, error } = await addNutritionMaster(payload);
    if (data) {
      toast.success(data?.message);
      onReflect?.();
      onClose();
    } else {
      if (error && error.error) {
        const values = getValues();
        const fieldNames = Object.keys(values);
        toast.error(<div dangerouslySetInnerHTML={{ __html: error.message || t('App.unexpected_error') }}></div>);
        for (const key of Object.keys(error.error)) {
          if (fieldNames.includes(key)) {
            setError(key as keyof NutritionMasterAddPayload, { type: 'custom', message: error.error[key] });
          }
        }
      }
    }
  };

  return (
    <Modal
      show
      size="lg"
      dialogClassName="nutrition-master-form-modal"
      backdrop="static"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={onClose}
    >
      <Modal.Header className="border-bottom-0 justify-content-end">
        <Button variant="primary" className="btn--auto" onClick={onClose}>
          <LiaTimesSolid size={16} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Form.Group as={Row} className="mb-3" controlId="nutrition_name">
            <Form.Label column sm="3" className="text-end">
              {t('NutritionMaster.nutrition_name')}
            </Form.Label>
            <Col sm="9">
              <Form.Control
                {...register('nutrition_name')}
                type="text"
                placeholder=""
                isInvalid={!!errors.nutrition_name}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">{errors.nutrition_name?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="nutrition_unit_master">
            <Form.Label column sm="3" className="text-end">
              {t('NutritionMaster.nutrition_unit_master')}
            </Form.Label>
            <Col sm="9">
              <Form.Select {...register('nutrition_unit_master')} style={{ width: '80px' }}>
                {nutritionUnitMasterList.map((nutritionUnit) => (
                  <option key={nutritionUnit.nutrition_unit_cd} value={nutritionUnit.nutrition_unit_cd}>
                    {nutritionUnit.nutrition_unit_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.nutrition_unit_master?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="display_order">
            <Form.Label column sm="3" className="text-end">
              {t('NutritionMaster.display_order')}
            </Form.Label>
            <Col sm="9">
              <Form.Control
                {...register('display_order')}
                className="text-end"
                style={{ width: '80px' }}
                type="number"
                placeholder=""
                defaultValue={nextDisplayOrder}
                min={1}
                isInvalid={!!errors.display_order}
              />
              <Form.Control.Feedback type="invalid">{errors.display_order?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <div className="text-center mt-5">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="d-inline-flex gap-1 justify-content-center btn btn-primary align-items-center"
            >
              <span>{t('NutritionMaster.add')}</span>
              {isSubmitting && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default NutritionMasterForm;
